/**
 * useEltJobExecutions Hook
 *
 * React Query hook for fetching job executions with server-side
 * sorting and offset-based pagination.
 */

import { useQuery } from "@tanstack/react-query";
import { jobExecutionService } from "../../../api/job-execution.service";
import type { JobExecutionStats, ListJobExecutionsResponse } from "../../../api/job-execution.service";
import type { EltJobExecutionListQuery } from "../../../types/elt-job-execution.types";

export const JOB_EXECUTIONS_QUERY_KEY = "jobExecutions";

const DEFAULT_STATS: JobExecutionStats = { total: 0, succeeded: 0, failed: 0, inProgress: 0, cancelled: 0 };

export function useEltJobExecutions(tenantId: string, query?: EltJobExecutionListQuery) {
  const result = useQuery<ListJobExecutionsResponse, Error>({
    queryKey: [JOB_EXECUTIONS_QUERY_KEY, tenantId, query],
    queryFn: () => jobExecutionService.listEltJobExecutions(tenantId, query),
    enabled: !!tenantId,
    staleTime: 10000,
    refetchInterval: 30000,
  });

  return {
    ...result,
    executions: result.data?.items ?? [],
    pagination: result.data?.pagination ?? { total: 0, page: 1, totalPages: 1, hasMore: false },
    stats: result.data?.stats ?? DEFAULT_STATS,
  };
}
